import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { CatalogItem } from '../../../redux/api/fymAPI.types';

/**
 * The services a release that is out should have a link on. A release with no link on one of them
 * counts as "missing links" on Home (design a1: "No Apple Music or Tidal link").
 */
export const HOME_CORE_LINK_SERVICES: readonly string[] = [
  MusicAPIIntegrationID.Spotify,
  MusicAPIIntegrationID.AppleMusic,
  MusicAPIIntegrationID.YoutubeMusic,
  MusicAPIIntegrationID.AmazonMusic,
  MusicAPIIntegrationID.Deezer,
  MusicAPIIntegrationID.Tidal,
];

/** The most rows the attention list shows (design a1). */
export const CATALOG_ATTENTION_ROWS = 3;

function releaseTime(item: CatalogItem): number {
  if (item.releaseDate === null) {
    return Number.NEGATIVE_INFINITY;
  }
  const time = Date.parse(item.releaseDate);
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

/**
 * The latest release: the newest release date that is not in the future. When every row is still
 * to come (or has no date), the newest row of all. `undefined` for an empty catalog.
 */
export function pickLatestRelease(items: CatalogItem[], now: Date): CatalogItem | undefined {
  const byDate = [...items].sort((a, b) => releaseTime(b) - releaseTime(a));
  const out = byDate.find((item) => {
    const time = releaseTime(item);
    return time !== Number.NEGATIVE_INFINITY && time <= now.getTime();
  });
  return out ?? byDate[0];
}

/**
 * The core services a release has no link on. Only a release that is out counts: it has a release
 * date on or before `now`. An unreleased or undated row returns none. iOS and Android use the
 * same rule.
 */
export function missingLinkServices(item: CatalogItem, now: Date): string[] {
  const time = releaseTime(item);
  if (item.kind === CatalogItemKind.unreleased || time === Number.NEGATIVE_INFINITY || time > now.getTime()) {
    return [];
  }
  const links = item.serviceLinks ?? {};
  return HOME_CORE_LINK_SERVICES.filter((service) => {
    const url = links[service];
    return url === undefined || url.trim() === '';
  });
}

export interface CatalogCounts {
  releases: number;
  missingLinks: number;
  noSmartlink: number;
}

/** The three numbers of the Catalog block. */
export function catalogCounts(items: CatalogItem[], now: Date): CatalogCounts {
  return {
    releases: items.length,
    missingLinks: items.filter((item) => missingLinkServices(item, now).length > 0).length,
    noSmartlink: items.filter((item) => item.smartLinkId === null).length,
  };
}

export type CatalogAttentionReason = { kind: 'missingLinks'; services: string[] } | { kind: 'noSmartlink' };

export interface CatalogAttentionRow {
  item: CatalogItem;
  reason: CatalogAttentionReason;
}

/**
 * Up to three rows that need work, newest first: missing links before a missing smartlink. The
 * latest release is left out, because the block already shows it above.
 */
export function catalogAttentionRows(
  items: CatalogItem[],
  latest: CatalogItem | undefined,
  now: Date
): CatalogAttentionRow[] {
  const others = [...items]
    .filter((item) => item.uuid !== latest?.uuid)
    .sort((a, b) => releaseTime(b) - releaseTime(a));
  const missing: CatalogAttentionRow[] = others
    .map((item) => ({ item, services: missingLinkServices(item, now) }))
    .filter(({ services }) => services.length > 0)
    .map(({ item, services }) => ({ item, reason: { kind: 'missingLinks', services } }));
  const noLink: CatalogAttentionRow[] = others
    .filter((item) => item.smartLinkId === null && missingLinkServices(item, now).length === 0)
    .map((item) => ({ item, reason: { kind: 'noSmartlink' } }));
  return [...missing, ...noLink].slice(0, CATALOG_ATTENTION_ROWS);
}
